/* The Finer Things Market — product stage.
   Supplier photos shot on white are cut out in the browser and set on the
   dark, gold-lit stage the banners use. Nothing in Shopify changes: the
   original image stays in the product, the theme just shows it differently.

   Only white-background shots are touched -- a photo whose border is not
   almost entirely white (a lifestyle shot, a dark product photo) is left
   exactly as it is. Any failure leaves the original in place. */
(() => {
  if (window.__finerStage) return;
  window.__finerStage = true;

  const TARGETS = [
    '.card__media img',                       // product + collection cards, search results
    '.product__media img',                    // product page gallery
    '.product-media-modal__content img',      // zoomed view
    '.cart-item__image',                      // cart page
    '.cart-notification-product__image img',  // "added to cart" popup
    '.predictive-search__image',              // search-as-you-type
    '.thumbnail img',                         // product page thumbnails
  ].join(',');

  const cache = new Map();          // src -> Promise<blobURL | null>
  const why = new Map();            // src -> reason it was left alone (for diagnosis)
  const queue = []; let busy = false;

  const baseSrc = img => {
    const s = img.currentSrc || img.src || '';
    if (!s || s.startsWith('blob:') || s.startsWith('data:')) return '';
    return s;
  };
  // Ask Shopify's CDN for a sensible processing size.
  const sized = (src, w) => {
    try { const u = new URL(src, location.href); u.searchParams.set('width', String(w)); return u.href; } catch (_) { return src; }
  };

  function cutout(src, maxW) {
    if (cache.has(src)) return cache.get(src);
    const job = new Promise(resolve => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => {
        try {
          const scale = Math.min(1, maxW / im.naturalWidth);
          const w = Math.max(1, Math.round(im.naturalWidth * scale)), h = Math.max(1, Math.round(im.naturalHeight * scale));
          const c = document.createElement('canvas'); c.width = w; c.height = h;
          const x = c.getContext('2d', { willReadFrequently: true });
          x.drawImage(im, 0, 0, w, h);
          const d = x.getImageData(0, 0, w, h), px = d.data, N = w * h;
          const minc = new Uint8Array(N), white = new Uint8Array(N);
          for (let i = 0, p = 0; i < N; i++, p += 4) {
            const r = px[p], g = px[p + 1], b = px[p + 2];
            const mn = r < g ? (r < b ? r : b) : (g < b ? g : b), mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
            minc[i] = mn;
            white[i] = (mn >= 246 && mx - mn < 10) ? 1 : 0;
          }
          // Is this a white-background shot? Look at a 2px frame around the edge.
          let edge = 0, edgeWhite = 0;
          const tally = i => { edge++; edgeWhite += white[i]; };
          for (let xx = 0; xx < w; xx++) { tally(xx); tally(w + xx); tally((h - 1) * w + xx); tally((h - 2) * w + xx); }
          for (let yy = 0; yy < h; yy++) { tally(yy * w); tally(yy * w + 1); tally(yy * w + w - 1); tally(yy * w + w - 2); }
          if (edgeWhite / edge < 0.86) { why.set(src, 'border ' + Math.round(100 * edgeWhite / edge) + '% white'); resolve(null); return; }
          // Background = white connected to the border (so whites INSIDE the product stay).
          const bg = new Uint8Array(N), st = new Int32Array(N); let top = 0;
          const push = i => { if (white[i] && !bg[i]) { bg[i] = 1; st[top++] = i; } };
          for (let xx = 0; xx < w; xx++) { push(xx); push((h - 1) * w + xx); }
          for (let yy = 0; yy < h; yy++) { push(yy * w); push(yy * w + w - 1); }
          while (top) {
            const i = st[--top], xx = i % w;
            if (xx > 0) push(i - 1); if (xx < w - 1) push(i + 1);
            if (i >= w) push(i - w); if (i < N - w) push(i + w);
          }
          // Rings of foreground by distance from the backdrop: ring 1 is the anti-aliased edge,
          // ring 3 is the product's own colour -- unless a shadow or a white part of the product
          // touches the backdrop, in which case ring 3 is still light and the cut would look ragged.
          const ring = new Uint8Array(N);   // 0 = backdrop or deep inside, 1..3 = distance
          let fgCount = 0;
          for (let i = 0; i < N; i++) if (!bg[i]) fgCount++;
          const near = (i, test) => { const xx = i % w; return (xx > 0 && test(i - 1)) || (xx < w - 1 && test(i + 1)) || (i >= w && test(i - w)) || (i < N - w && test(i + w)); };
          for (let i = 0; i < N; i++) if (!bg[i] && near(i, j => bg[j])) ring[i] = 1;
          for (const k of [2, 3]) for (let i = 0; i < N; i++) if (!bg[i] && !ring[i] && near(i, j => ring[j] === k - 1)) ring[i] = k;
          let r3 = 0, r3Light = 0;
          for (let i = 0; i < N; i++) if (ring[i] === 3) { r3++; if (minc[i] > 222) r3Light++; }
          if (r3 && r3Light / r3 > 0.22) { why.set(src, 'light rim ' + Math.round(100 * r3Light / r3) + '%'); resolve(null); return; }
          // A white product on a white backdrop (white cable ties, a white jacket): the cut
          // would eat into the product itself, so show the photo as it is.
          let fgLight = 0;
          for (let i = 0; i < N; i++) if (!bg[i] && minc[i] > 205) fgLight++;
          if (fgCount && fgLight / fgCount > 0.42) { why.set(src, 'light product ' + Math.round(100 * fgLight / fgCount) + '%'); resolve(null); return; }
          if (fgCount < N * 0.03 || fgCount > N * 0.97) { why.set(src, 'product ' + Math.round(100 * fgCount / N) + '% of frame'); resolve(null); return; }   // nothing to stage
          // Alpha: background out; the product's light rim feathered so no white halo.
          for (let i = 0, p = 3; i < N; i++, p += 4) {
            if (bg[i]) { px[p] = 0; continue; }
            if (ring[i] === 1 && minc[i] > 190) px[p] = Math.round(255 * Math.max(0.12, Math.min(1, (248 - minc[i]) / 58)));
            else if (ring[i] === 2 && minc[i] > 225) px[p] = Math.round(255 * Math.max(0.5, Math.min(1, (250 - minc[i]) / 30)));
          }
          x.putImageData(d, 0, 0);
          c.toBlob(b => resolve(b ? URL.createObjectURL(b) : null), 'image/png');
        } catch (e) { why.set(src, 'error ' + e.name); resolve(null); }       // tainted canvas, decode error: keep the original
      };
      im.onerror = () => { why.set(src, 'load failed'); resolve(null); };
      im.src = src;
    });
    cache.set(src, job);
    return job;
  }

  function stage(img) {
    if (img.dataset.fnStage) return;
    const src = baseSrc(img); if (!src) return;
    img.dataset.fnStage = 'pending';
    const big = !!img.closest('.product__media, .product-media-modal__content');
    queue.push({ img, src: sized(src, big ? 1400 : 700), maxW: big ? 1400 : 700 });
    pump();
  }
  function pump() {
    if (busy || !queue.length) return;
    busy = true;
    const { img, src, maxW } = queue.shift();
    cutout(src, maxW).then(url => {
      if (url && img.isConnected) {
        img.dataset.fnOrigSrcset = img.getAttribute('srcset') || '';
        img.dataset.fnOrigSrc = img.getAttribute('src') || '';
        img.removeAttribute('srcset');
        img.src = url;
        img.dataset.fnStage = 'staged';
        const holder = img.closest('.card__inner, .product__media, .product-media-modal__content, .cart-item__media, .cart-notification-product__image, .predictive-search__item, .thumbnail, .media') || img.parentElement;
        holder.classList.add('fn-staged');
      } else {
        img.dataset.fnStage = 'skip'; img.dataset.fnWhy = why.get(src) || (img.isConnected ? 'unknown' : 'removed from page');
        // A white-background shot that could not be cut cleanly: present it framed on the
        // stage instead, so it does not glare next to the staged ones. Lifestyle photos
        // (border not white) are left full-bleed.
        const bw = (img.dataset.fnWhy.match(/^border (\d+)% white/) || [])[1];
        if ((/^light/.test(img.dataset.fnWhy) || (bw && +bw >= 45)) && img.isConnected) {
          img.dataset.fnStage = 'framed';
          const holder = img.closest('.card__inner, .product__media, .product-media-modal__content, .cart-item__media, .cart-notification-product__image, .predictive-search__item, .thumbnail, .media') || img.parentElement;
          holder.classList.add('fn-staged', 'fn-framed');
        }
      }
    }).finally(() => {
      busy = false;
      // Yield to the page between images so scrolling never stutters.
      (window.requestIdleCallback || (f => setTimeout(f, 16)))(pump);
    });
  }

  // Process images as they come near the screen; re-scan when Shopify swaps markup.
  const io = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => {
    if (!e.isIntersecting) return; io.unobserve(e.target);
    const img = e.target;
    if (img.complete && img.naturalWidth) stage(img); else img.addEventListener('load', () => stage(img), { once: true });
  }), { rootMargin: '400px 0px' }) : null;
  const scan = (root = document) => {
    root.querySelectorAll(TARGETS).forEach(img => {
      if (img.dataset.fnStage || img.dataset.fnSeen) return;
      img.dataset.fnSeen = '1';
      if (io) io.observe(img); else stage(img);
    });
  };
  scan();
  new MutationObserver(ms => {
    for (const m of ms) for (const n of m.addedNodes) if (n.nodeType === 1) {
      if (n.matches && n.matches(TARGETS)) scan(n.parentElement || document); else if (n.querySelector) scan(n);
    }
  }).observe(document.body, { childList: true, subtree: true });
  // Shared with finer-spin.js so every frame of a spin is staged the same way.
  window.__finerStageAPI = { cutout, sized };
  window.__finerStageStats = () => {
    const all = [...document.querySelectorAll('[data-fn-stage]')];
    return { framed: all.filter(i => i.dataset.fnStage === 'framed').length, staged: all.filter(i => i.dataset.fnStage === 'staged').length, skipped: all.filter(i => i.dataset.fnStage === 'skip').length, pending: all.filter(i => i.dataset.fnStage === 'pending').length };
  };
})();
